// ---------------------------------------------------------------------------
// Tutorial v3 — the progression prototype's scripted first war
// ---------------------------------------------------------------------------
//
// Tutorial v2's machinery (tutorialV2.js, tutorialV2Steps.js) on a war of one
// structure each: an Outpost. The first kill wins. Both sides are scripted
// and every round plays through the real rules, as in v2. It covers deploying,
// attacking and aiming, reading clues with the eye, protecting your Outpost
// (a block), building a brood, and moving into a corridor to attack from it
// in the same round.
//
// THE HUNT reuses v2's geometry: their Outpost sits where v2's A did, and
// v2's probe and kill hexes still stamp A:6 and A:3 and cross only at it
// (v2's other structures never decided those stamps: A was the nearest to
// every probe).
//
// Corridors are numbered 1–7, as the player sees them.

import { makeHomefront, placeStructure, subIndex } from '../rules/subplotGrid'
import { blankGameBoard } from '../rules/BlankGameBoard'
import { TUTORIAL_V2_CAPACITIES, TUTORIAL_V2_TARGETS, TUTORIAL_FIRST_ATTACK_ROUND, playScriptedRound } from './tutorialV2'

const hex = (sc, sr) => subIndex(sc, sr)

export const TUTORIAL_V3_CAPACITIES = TUTORIAL_V2_CAPACITIES
// One structure each: the first kill wins.
export const TUTORIAL_V3_CONFIG = { structuresToWin: 1 }

// Their Outpost: v2's A. Yours: corridor 1, half way down.
export const TUTORIAL_V3_OPP_OUTPOST = hex(12, 4)
export const TUTORIAL_V3_SELF_OUTPOST = hex(1, 6)

function outpostHive(id, s) {
    const hf = makeHomefront(() => null)
    placeStructure(hf, id, [s])
    Object.assign(hf.structures[id], { name: 'Outpost', level: 1 })
    return hf
}
export const makeTutorialV3OppHive = () => outpostHive('opp-outpost', TUTORIAL_V3_OPP_OUTPOST)
export const makeTutorialV3SelfHive = () => outpostHive('self-outpost', TUTORIAL_V3_SELF_OUTPOST)

// The one hex each of the player's strikes is pointed at.
export const TUTORIAL_V3_TARGETS = {
    probe1: TUTORIAL_V2_TARGETS.probe1,   // corridor 3: stamps A:6
    probe2: TUTORIAL_V2_TARGETS.probe2,   // corridor 4: stamps A:3
    kill:   TUTORIAL_V3_OPP_OUTPOST,      // corridor 5: where the rings cross
}

// The opponent's strike on the player's empty corridor 2 (a clue near their
// Outpost, which the blast doesn't reach).
export const TUTORIAL_V3_OPP_PROBE = hex(4, 3)

// Per round, per side (tutorialV2Orders' plan shape). The player is offered
// only what's listed; the opponent attacks only where the lesson needs it.
export const TUTORIAL_V3_SCRIPT = [
    { round: 1,
      self: { deploy: [3, 4] },
      opp:  { deploy: [2, 6] } },
    { round: 2,   // no attacks until round 3: both sides build up
      self: { deploy: [7, 7] },
      opp:  { deploy: [1, 5] } },
    { round: 3,   // both your probes at once; they strike your empty corridor 2,
                  // and deploy clear of corridors 3 and 4 so both probes land
      self: { attack: [{ corridor: 3 }, { corridor: 4 }], deploy: [5, 6] },
      opp:  { attack: { corridor: 2, aim: TUTORIAL_V3_OPP_PROBE }, deploy: [6, 6] } },
    { round: 4,   // block corridor 1 (a 1–1 tie at your Outpost); build a brood in corridor 7
      self: { brood: { corridor: 7, row: 4 }, deploy: [1, 3] },
      opp:  { attack: { corridor: 1, aim: TUTORIAL_V3_SELF_OUTPOST }, deploy: [6, 2] } },
    { round: 5,   // move from 6 into 5, attack 2 against 1 where the rings cross
      self: { move: { from: 6, dir: 'left' }, attack: { corridor: 5 }, deploy: [3, 4] },
      opp:  { deploy: [2, 2] } },
]

export function createTutorialV3War() {
    const player = (homefront) => ({ board: blankGameBoard(TUTORIAL_V3_CAPACITIES, { firstAttackRound: TUTORIAL_FIRST_ATTACK_ROUND }), homefront, structuresLost: 0, yield: 0 })
    return { round: 2, p1: player(makeTutorialV3SelfHive()), p2: player(makeTutorialV3OppHive()) }
}

// Round `index` with the player's own orders; the opponent plays its script.
export const playTutorialV3RoundWith = (war, index, selfOrders) =>
    playScriptedRound(TUTORIAL_V3_SCRIPT, war, index, selfOrders, TUTORIAL_V3_CONFIG)
